import { useState, useEffect } from 'react';
import { Star, Heart, Eye, TrendingUp, Flame, Award } from 'lucide-react';
import API_URL from "../config/ApiUrl";
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../arrangement/ThemeContext';
import { getProductImage } from '../static/ProductPlaceHolder';
export default function SponsoredProductsSection() {
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [liked, setLiked] = useState({});
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useAdminProducts, setUseAdminProducts] = useState(false);
  const navigate = useNavigate();
  const { colors } = useTheme();

  const isMobile = window.innerWidth <= 768;

  // Fetch featured products from backend
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/featured-products`, {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (data.success && data.data.products) {
          // Transform backend data to match component format
          const transformedProducts = data.data.products.map(product => ({
            id: product.id,
            brand: product.brand,
            name: product.name,
            rating: product.rating || 0,
            reviewCount: product.reviewCount || 0,
            price: product.attributes?.price || "$0", // Assuming price is in attributes
            pricing: product.pricing || {
              has_pricing: false,
              average_price: null,
              min_price: null,
              max_price: null,
              location_count: 0
            },
            originalPrice: product.attributes?.originalPrice || null,
            discount: product.attributes?.discount || null,
            image: product.coverImage?.imageUrl,
            category: product.type,
            isNew: product.attributes?.isNew || false,
            isTrending: product.rating >= 4.5 && product.reviewCount >= 50, // Auto-determine trending
            description: product.attributes?.description || `Quality ${product.type.toLowerCase()} from ${product.brand}`,
            isAdminFeatured: product.isAdminFeatured || false
          }));
          
          setFeaturedProducts(transformedProducts);
          setUseAdminProducts(data.data.useAdminProducts);
        }
      } catch (error) {
        console.error('Failed to fetch featured products:', error);
        // Fallback to mock data if API fails
        setFeaturedProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

    const handleProductClick = (product) => (e) => {
      // Prevent navigation if clicking on wishlist button or other interactive elements
      console.log("+===============================================================================")

      
      console.log('Product slug:', product.slug);
      console.log('Product ID:', product.id);
      console.log('Full product:', product);
      
      // Navigate to the correct path that matches your router
      navigate(`/products/${product.id}`);
    };


  // Auto-rotate hero product every 4 seconds
  useEffect(() => {
    if (featuredProducts.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [featuredProducts.length]);

  const handleLike = (productId) => {
    setLiked(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  if (loading) {
    return (
      <section style={{ marginBottom: '4rem' }}>
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          color: '#888'
        }}>
          Loading featured products...
        </div>
      </section>
    );
  }

  if (featuredProducts.length === 0) {
    return (
      <section style={{ marginBottom: '4rem' }}>
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '1rem',
          color: '#888'
        }}>
          <h3 style={{ color: 'white', marginBottom: '1rem' }}>No Featured Products Available</h3>
          <p>Check back soon for exciting product recommendations!</p>
        </div>
      </section>
    );
  }

  const featuredProduct = featuredProducts[currentSlide];

  return (
    <section style={{ marginBottom: '4rem' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '2rem'
      }}>
        <h2 style={{
          fontSize: '2rem',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 50%, #059669 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          ✨ Featured Products
        </h2>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          {useAdminProducts && (
            <div style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '2rem',
              fontSize: '0.75rem',
              color: colors.pinCard,
              fontWeight: '600'
            }}>
              Staff Curated
            </div>
          )}
          <div style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'rgba(0, 212, 170, 0.1)',
            border: '1px solid rgba(0, 212, 170, 0.3)',
            borderRadius: '2rem',
            fontSize: '0.875rem',
            color: '#00d4aa',
            fontWeight: '600'
          }}>
            Trusted by Our Staff
          </div>
        </div>
      </div>

      {/* Hero Product Carousel */}
      <div style={{
        position: 'relative',
        marginBottom: isMobile ? '1.5rem' : '2.5rem',
        background: 'linear-gradient(135deg, rgba(0, 212, 170, 0.15) 0%, rgba(16, 185, 129, 0.1) 50%, rgba(5, 150, 105, 0.05) 100%)',
        border: '1px solid rgba(0, 212, 170, 0.3)',
        borderRadius: '1.5rem',
        padding: isMobile ? '1.5rem' : '2rem',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(0, 212, 170, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />

        <div style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', // Stack on mobile
          alignItems: 'center',
          gap: isMobile ? '2rem' : '3rem'
        }}>
          {/* Product Info */}
          <div style={{ order: isMobile ? 2 : 1 }}> {/* Image first on mobile */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '0.5rem' : '1rem',
              marginBottom: '1rem',
              flexWrap: 'wrap'
            }}>
              <div style={{
                padding: isMobile ? '0.375rem 0.75rem' : '0.5rem 1rem',
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                color: 'black',
                borderRadius: '2rem',
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Featured Deal
              </div>
              {featuredProduct.isNew && (
                <div style={{
                  padding: isMobile ? '0.2rem 0.5rem' : '0.25rem 0.75rem',
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                  color: '#ef4444',
                  borderRadius: '1rem',
                  fontSize: isMobile ? '0.625rem' : '0.75rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <Flame size={isMobile ? 10 : 12} />
                  NEW
                </div>
              )}
              {featuredProduct.isAdminFeatured && (
                <div style={{
                  padding: isMobile ? '0.2rem 0.5rem' : '0.25rem 0.75rem',
                  backgroundColor: 'rgba(251, 191, 36, 0.2)',
                  color: '#fbbf24',
                  borderRadius: '1rem',
                  fontSize: isMobile ? '0.625rem' : '0.75rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <Award size={isMobile ? 10 : 12} />
                  STAFF PICK
                </div>
              )}
            </div>

            <h3 style={{
              fontSize: isMobile ? '1.75rem' : '2.5rem',
              fontWeight: '800',
              margin: '0 0 0.5rem 0',
              color: 'white',
              lineHeight: '1.1'
            }}>
              {featuredProduct.name}
            </h3>

            <p style={{
              color: '#00d4aa',
              fontSize: isMobile ? '1rem' : '1.125rem',
              fontWeight: '600',
              margin: '0 0 1rem 0'
            }}>
              by {featuredProduct.brand}
            </p>

            <p style={{
              color: colors.textPrimary1,
              fontSize: isMobile ? '0.875rem' : '1rem',
              lineHeight: '1.6',
              margin: '0 0 1.5rem 0'
            }}>
              {featuredProduct.description}
            </p>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '1rem' : '1.5rem',
              marginBottom: '2rem',
              flexWrap: 'wrap'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Star fill="#fbbf24" color="#fbbf24" size={isMobile ? 18 : 20} />
                <span style={{ fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: '700', color: 'white' }}>
                  {featuredProduct.rating}
                </span>
                <span style={{ fontSize: isMobile ? '0.75rem' : '0.875rem', color: '#888' }}>
                  ({featuredProduct.reviewCount} reviews)
                </span>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '2rem',
              flexWrap: 'wrap'
            }}>
              <div style={{
                fontSize: isMobile ? '1.5rem' : '2rem',
                fontWeight: '800',
                color: '#00d4aa'
              }}>
                {`$${featuredProduct?.pricing?.average_price.toFixed(2)}`||featuredProduct.price}
              </div>
              <span style={{
                fontSize: '10px',
                padding: '2px 6px',
                background: 'rgba(0, 212, 170, 0.2)',
                color: '#00d4aa',
                borderRadius: '4px',
                fontWeight: '600'
              }}>
                AVG
              </span>
              {featuredProduct.originalPrice && (
                <>
                  <div style={{
                    fontSize: isMobile ? '1rem' : '1.25rem',
                    color: '#666',
                    textDecoration: 'line-through'
                  }}>
                    {featuredProduct.originalPrice}
                  </div>
                  {featuredProduct.discount && (
                    <div style={{
                      padding: isMobile ? '0.375rem 0.75rem' : '0.5rem 1rem',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      borderRadius: '0.5rem',
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      fontWeight: '700'
                    }}>
                      {featuredProduct.discount}
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{
              display: 'flex',
              gap: isMobile ? '0.75rem' : '1rem',
              flexDirection: isMobile ? 'column' : 'row' // Stack buttons on mobile
            }}>
              <button 
              onClick={handleProductClick(featuredProduct)}
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                color: 'black',
                border: 'none',
                borderRadius: '0.75rem',
                padding: isMobile ? '0.875rem 1.5rem' : '1rem 2rem',
                fontSize: isMobile ? '0.875rem' : '1rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: 'translateY(0)',
                boxShadow: '0 4px 20px rgba(0, 212, 170, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 212, 170, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 212, 170, 0.3)';
              }}
              >
                View Details
              </button>
              
              <button 
                onClick={() => handleLike(featuredProduct.id)}
                style={{
                  padding: isMobile ? '0.875rem' : '1rem',
                  backgroundColor: liked[featuredProduct.id] ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                  border: `1px solid ${liked[featuredProduct.id] ? '#ef4444' : 'rgba(255, 255, 255, 0.2)'}`,
                  borderRadius: '0.75rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: isMobile ? 'auto' : '60px' // Full width on mobile when stacked
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <Heart 
                  size={20} 
                  fill={liked[featuredProduct.id] ? "#ef4444" : "none"} 
                  color={liked[featuredProduct.id] ? "#ef4444" : "white"} 
                />
                {isMobile && <span style={{ marginLeft: '0.5rem', color: 'white' }}>Like</span>}
              </button>
            </div>
          </div>

          {/* Product Visual */}
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            order: isMobile ? 1 : 2 // Image first on mobile
          }}>
            <div style={{
              width: isMobile ? '250px' : '300px',
              height: isMobile ? '250px' : '300px',
              background: 'linear-gradient(135deg, rgba(0, 212, 170, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
              borderRadius: '2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(0, 212, 170, 0.3)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease'
            }}>
              {/* Floating elements */}
              <div style={{
                position: 'absolute',
                top: isMobile ? '15px' : '20px',
                right: isMobile ? '15px' : '20px',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '1rem',
                padding: '0.5rem',
                backdropFilter: 'blur(10px)'
              }}>
                <TrendingUp size={isMobile ? 14 : 16} color="#00d4aa" />
              </div>
              
              <img 
                src={featuredProduct.image|| getProductImage(featuredProduct.category)}
                alt={featuredProduct.name}
                style={{
                  width: isMobile ? '170px' : '200px',
                  height: isMobile ? '170px' : '200px',
                  borderRadius: '1rem',
                  objectFit: 'cover',
                  filter: 'brightness(1.1) contrast(1.1)',
                  transition: 'all 0.3s ease'
                }}
              />
              
              {/* Glow effect */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at center, rgba(0, 212, 170, 0.2) 0%, transparent 70%)',
                animation: 'pulse 3s ease-in-out infinite'
              }} />
            </div>
            
            {/* Floating badges */}
            {featuredProduct.isTrending && (
              <div style={{
                position: 'absolute',
                top: '-10px',
                left: '-10px',
                backgroundColor: '#ef4444',
                color: 'white',
                padding: isMobile ? '0.375rem 0.75rem' : '0.5rem 1rem',
                borderRadius: '2rem',
                fontSize: isMobile ? '0.625rem' : '0.75rem',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                boxShadow: '0 4px 20px rgba(239, 68, 68, 0.4)',
                animation: 'float 3s ease-in-out infinite'
              }}>
                🔥 TRENDING
              </div>
            )}
          </div>
        </div>

        {/* Carousel Indicators */}
        {featuredProducts.length > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginTop: isMobile ? '1.5rem' : '2rem'
          }}>
            {featuredProducts.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                style={{
                  width: currentSlide === index ? (isMobile ? '1.5rem' : '2rem') : '0.75rem',
                  height: '0.75rem',
                  backgroundColor: currentSlide === index ? '#00d4aa' : 'rgba(255, 255, 255, 0.3)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Product Grid */}
      {featuredProducts.length > 1 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem'
        }}>
          {featuredProducts.map((product) => (
            <div
              key={product.id}
              onClick={handleProductClick(product)} 
              onMouseEnter={() => setHoveredProduct(product.id)}
              onMouseLeave={() => setHoveredProduct(null)}
              style={{
                backgroundColor: '#1a1a1a',
                border: `1px solid ${hoveredProduct === product.id ? '#00d4aa' : '#333'}`,
                borderRadius: '1rem',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                transform: hoveredProduct === product.id ? 'translateY(-4px)' : 'translateY(0)',
                boxShadow: hoveredProduct === product.id ? '0 20px 40px rgba(0, 212, 170, 0.15)' : '0 4px 20px rgba(0, 0, 0, 0.3)',
                cursor: 'pointer',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              {/* Product Image */}
              <div style={{
                position: 'relative',
                backgroundColor: '#2a2a2a',
                height: '200px',
                overflow: 'hidden'
              }}>
                <img
                  src={product.image||getProductImage(product.category)}
                  alt={product.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'all 0.3s ease',
                    transform: hoveredProduct === product.id ? 'scale(1.05)' : 'scale(1)'
                  }}
                />
                
                {/* Overlay badges */}
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  left: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: 'rgba(0, 212, 170, 0.9)',
                    color: 'black',
                    borderRadius: '1rem',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    textTransform: 'uppercase'
                  }}>
                    FEATURED
                  </div>
                  
                  {product.discount && (
                    <div style={{
                      padding: '0.25rem 0.75rem',
                      backgroundColor: 'rgba(239, 68, 68, 0.9)',
                      color: 'white',
                      borderRadius: '1rem',
                      fontSize: '0.75rem',
                      fontWeight: '700'
                    }}>
                      {product.discount}
                    </div>
                  )}
                  
                  {product.isAdminFeatured && (
                    <div style={{
                      padding: '0.25rem 0.75rem',
                      backgroundColor: 'rgba(251, 191, 36, 0.9)',
                      color: 'black',
                      borderRadius: '1rem',
                      fontSize: '0.75rem',
                      fontWeight: '700'
                    }}>
                      STAFF PICK
                    </div>
                  )}
                </div>

                {/* Like button overlay */}
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLike(product.id);
                  }}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    padding: '0.5rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(10px)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                  }}
                >
                  <Heart 
                    size={16} 
                    fill={liked[product.id] ? "#ef4444" : "none"} 
                    color={liked[product.id] ? "#ef4444" : "white"} 
                  />
                </button>
              </div>

              {/* Product Details */}
              <div style={{ 
                padding: '1.5rem',
                // ADD THESE 3 LINES:
                display: 'flex',
                flexDirection: 'column',
                flex: 1
              }}>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#888',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  marginBottom: '0.5rem'
                }}>
                  {product.brand} • {product.category}
                </div>
                
                <h4 style={{
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  color: 'white',
                  margin: '0 0 1rem 0',
                  lineHeight: '1.3'
                }}>
                  {product.name}
                </h4>

                <p style={{
                  fontSize: '0.875rem',
                  color: '#bbb',
                  margin: '0 0 1rem 0',
                  lineHeight: '1.4'
                }}>
                  {product.description}
                </p>

                {/* Rating */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', gap: '1px' }}>
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        fill={i < Math.floor(product.rating) ? "#fbbf24" : "none"}
                        color="#fbbf24"
                      />
                    ))}
                  </div>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'white' }}>
                    {product.rating}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#888' }}>
                    ({product.reviewCount})
                  </span>
                </div>

                {/* Price and CTA */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  marginTop: 'auto'

                }}>
                  <div>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: '800',
                      color: '#00d4aa'
                    }}>
                      {`$${product?.pricing?.average_price.toFixed(2)}`||product.price}
                    </div>
                    <span style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      background: 'rgba(0, 212, 170, 0.2)',
                      color: '#00d4aa',
                      borderRadius: '4px',
                      fontWeight: '600'
                    }}>
                      AVG
                    </span>
                    {product.originalPrice && (
                      <div style={{
                        fontSize: '0.875rem',
                        color: '#666',
                        textDecoration: 'line-through'
                      }}>
                        {product.originalPrice}
                      </div>
                    )}
                  </div>
                  
                  <button 
                  style={{
                    backgroundColor: hoveredProduct === product.id ? '#00d4aa' : 'rgba(0, 212, 170, 0.2)',
                    color: hoveredProduct === product.id ? 'black' : '#00d4aa',
                    border: '1px solid #00d4aa',
                    borderRadius: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    whiteSpace: 'nowrap'
                  }}>
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bottom CTA */}
      <div style={{
        textAlign: 'center',
        marginTop: '3rem',
        padding: '2rem',
        background: 'linear-gradient(135deg, rgba(0, 212, 170, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
        border: '1px solid rgba(0, 212, 170, 0.2)',
        borderRadius: '1rem'
      }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: 'white',
          margin: '0 0 0.5rem 0'
        }}>
          Want to feature your product?
        </h3>
        <p style={{
          color: '#888',
          margin: '0 0 1.5rem 0',
          fontSize: '0.875rem'
        }}>
          Reach thousands of engaged community members with targeted sponsorships
        </p>
        <button style={{
          backgroundColor: 'transparent',
          color: '#00d4aa',
          border: '2px solid #00d4aa',
          borderRadius: '0.75rem',
          padding: '0.75rem 2rem',
          fontSize: '0.875rem',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#00d4aa';
          e.currentTarget.style.color = 'black';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#00d4aa';
        }}
        >
          Learn More About Sponsorship
        </button>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }
      `}</style>
    </section>
  );
}